<template>
  <b-modal
    v-model="show"
    scrollable
    :size="finalizado ? 'xl' : 'lg'"
    no-close-on-backdrop
    hide-footer
    centered
    :id="`boleto-batch`"
    modal-class="modern-modal boleto-modal"
    @hide="handleFecharModal"
  >
    <template v-slot:modal-title>
      <div class="modal-title-modern">
        <i class="fas fa-file-invoice-dollar mr-2"></i>
        Gerar Boletos em Lote
      </div>
    </template>

    <div class="boleto-content-modern">
      <!-- Loading State -->
      <div v-if="carregando" class="loading-section">
        <div class="loading-card">
          <div class="loading-icon">
            <i class="fas fa-cog fa-spin"></i>
          </div>
          <div class="loading-info">
            <h4 class="loading-title">Gerando Boletos</h4>
            <p class="loading-text">
              Processando boleto {{ progresso + 1 }} de {{ itens.length }}
            </p>
            <div class="progress-container">
              <div
                class="progress-bar"
                :style="{ width: ((progresso + 1) / itens.length) * 100 + '%' }"
              ></div>
            </div>
            <span class="progress-percentage">
              {{ Math.round(((progresso + 1) / itens.length) * 100) }}%
            </span>
          </div>
        </div>
      </div>

      <!-- Initial State -->
      <div v-else-if="!finalizado" class="initial-section">
        <div class="initial-card">
          <div class="initial-icon">
            <i class="fas fa-file-invoice-dollar"></i>
          </div>
          <div class="initial-content">
            <h3 class="initial-title">Pronto para Gerar Boletos</h3>
            <p class="initial-description">
              {{ itens.length }} boleto{{ itens.length > 1 ? "s" : "" }} será{{
                itens.length > 1 ? "ão" : ""
              }}
              processado{{ itens.length > 1 ? "s" : "" }}
            </p>
            <div class="initial-stats">
              <div class="stat-item">
                <span class="stat-label">Total de Boletos</span>
                <span class="stat-value">{{ itens.length }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="action-section">
          <button
            class="btn-generate-modern"
            @click="gerarTodos"
            :disabled="carregando"
          >
            <i class="fas fa-magic mr-2"></i>
            {{ carregando ? "Gerando..." : "Gerar Todos os Boletos" }}
          </button>
        </div>
      </div>

      <!-- Results State -->
      <div v-if="finalizado" class="results-section">
        <div class="results-header">
          <h3 class="results-title">
            <i class="fas fa-check-circle mr-2"></i>
            Processamento Concluído
          </h3>
          <p class="results-summary">
            {{ resultados.filter((r) => r.sucesso).length }} de
            {{ resultados.length }} boletos gerados com sucesso
          </p>
        </div>

        <div class="results-grid">
          <div
            v-for="(resultado, index) in resultados"
            :key="(itens[index] && itens[index].id) || index"
            class="boleto-card"
            :class="{
              'boleto-success': resultado && resultado.sucesso,
              'boleto-error': !resultado || !resultado.sucesso,
            }"
          >
            <!-- Card Header -->
            <div class="boleto-card-header">
              <div class="boleto-status-icon">
                <i
                  :class="[
                    'fas',
                    resultado && resultado.sucesso
                      ? 'fa-check-circle'
                      : 'fa-exclamation-triangle',
                  ]"
                ></i>
              </div>
              <div class="boleto-number">
                Boleto {{ index + 1 }} de {{ itens.length }}
              </div>
            </div>

            <!-- Card Content -->
            <div class="boleto-card-content">
              <div class="boleto-info-section">
                <h4 class="boleto-client-name">
                  {{
                    (itens[index] &&
                      (itens[index].finance_client_name ||
                        itens[index].description)) ||
                    "Cliente não identificado"
                  }}
                </h4>

                <div class="boleto-details">
                  <div class="detail-item">
                    <i class="fas fa-calendar-alt mr-1"></i>
                    <span class="detail-label">Vencimento:</span>
                    <span class="detail-value">
                      {{ formatDate(itens[index].due_date, false) }}
                    </span>
                  </div>

                  <div class="detail-item">
                    <i class="fas fa-dollar-sign mr-1"></i>
                    <span class="detail-label">Valor:</span>
                    <span class="detail-value amount">
                      R$
                      {{
                        formatValueOnKeyUp(String(itens[index].totalCredito))
                      }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Actions or Error -->
              <div class="boleto-card-actions">
                <div
                  v-if="resultado && resultado.sucesso"
                  class="success-actions"
                >
                  <button
                    @click="visualizarBoleto(index)"
                    class="btn-action-view"
                  >
                    <i class="fas fa-eye mr-1"></i>
                    Visualizar
                  </button>

                  <button
                    @click="baixarBoleto(index)"
                    class="btn-action-download"
                  >
                    <i class="fas fa-download mr-1"></i>
                    Baixar PDF
                  </button>
                </div>

                <div v-else class="error-actions">
                  <span class="error-message">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Erro ao gerar boleto
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Results Summary -->
        <div class="results-footer">
          <div class="summary-stats">
            <div class="summary-item success-summary">
              <div class="summary-icon">
                <i class="fas fa-check-circle"></i>
              </div>
              <div class="summary-text">
                <span class="summary-number">{{
                  resultados.filter((r) => r.sucesso).length
                }}</span>
                <span class="summary-label">Sucessos</span>
              </div>
            </div>

            <div class="summary-item error-summary">
              <div class="summary-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <div class="summary-text">
                <span class="summary-number">{{
                  resultados.filter((r) => !r.sucesso).length
                }}</span>
                <span class="summary-label">Erros</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import moment from "moment";

import {
  gerarBoleto,
  baixarPDF,
  visualizarPDF,
} from "../../../../services/boletocloud/api";

import { formatValueOnKeyUp, formatDate } from "../../../../lib/format";

export default {
  name: "GenerateBoletoInBatch",

  props: {
    itens: Array,
    visible: { type: Boolean, default: true },
  },

  data() {
    return {
      show: this.visible,
      carregando: false,
      progresso: 0,
      resultados: [],
      pdfBlobs: [],
      finalizado: false,
    };
  },

  watch: {
    visible(visible) {
      this.show = visible;
    },
    internalVisible(show) {
      this.$emit("input", show);
    },
  },

  methods: {
    formatDate,
    formatValueOnKeyUp,

    async gerarTodos() {
      try {
        this.carregando = true;
        this.finalizado = false;
        this.progresso = 0;
        this.resultados = [];
        this.pdfBlobs = [];

        for (let i = 0; i < this.itens.length; i++) {
          this.progresso = i;

          let item = this.itens[i];
          let { id, due_date, totalCredito } = item;

          // Formatar valor
          let valor = formatValueOnKeyUp(String(totalCredito));
          valor = String(valor).replace(/\./g, "").replace(",", ".");

          const dadosPersonalizados = {
            id,
            emissao: moment().format("YYYY-MM-DD"),
            vencimento: moment(due_date).format("YYYY-MM-DD"),
            titulo: "DM",
            valor,
            instrucoes: [],
          };

          const resultado = await gerarBoleto(dadosPersonalizados);

          this.resultados.push(resultado);

          if (resultado.sucesso) {
            this.pdfBlobs.push(resultado.pdf);
          } else {
            this.pdfBlobs.push(null);
          }
        }

        this.carregando = false;
        this.$emit("gerado", true);
        this.finalizado = true;
      } catch (error) {
        this.carregando = false;
        this.$emit("gerado", true);
        this.finalizado = true;
        this.resultados.push({ sucesso: false, erro: error.message });
        this.pdfBlobs.push(null);
      }
    },

    handleFecharModal() {
      this.carregando = false;
      this.progresso = 0;
      this.resultados = [];
      this.pdfBlobs = [];
      this.finalizado = false;
      this.$emit("close");
    },

    baixarBoleto(index) {
      if (this.pdfBlobs[index]) {
        baixarPDF(this.pdfBlobs[index], "meu-boleto.pdf");
      }
    },

    visualizarBoleto(index) {
      if (this.pdfBlobs[index]) {
        visualizarPDF(this.pdfBlobs[index]);
      }
    },
  },
};
</script>

<style scoped>
/* Modal Styling */
.modal-title-modern {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.boleto-content-modern {
  padding: 8px 0;
}

/* Loading Section */
.loading-section {
  text-align: center;
  padding: 40px 20px;
}

.loading-card {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e5e7eb;
}

.loading-icon {
  margin-bottom: 20px;
}

.loading-icon i {
  font-size: 48px;
  color: #0f65b0;
}

.loading-info {
  margin-bottom: 24px;
}

.loading-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.loading-text {
  font-size: 16px;
  color: #64748b;
  margin: 0 0 20px 0;
}

.progress-container {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-percentage {
  font-size: 14px;
  font-weight: 600;
  color: #0f65b0;
}

/* Initial Section */
.initial-section {
  padding: 20px 0;
}

.initial-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.initial-icon {
  margin-bottom: 20px;
}

.initial-icon i {
  font-size: 48px;
  color: #0f65b0;
}

.initial-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.initial-description {
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
}

.initial-stats {
  display: flex;
  justify-content: center;
  gap: 24px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #0f65b0;
}

.action-section {
  text-align: center;
}

.btn-generate-modern {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 16px 32px !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3) !important;
  transition: all 0.3s ease !important;
  cursor: pointer;
  width: 100%;
  max-width: 300px;
}

.btn-generate-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4) !important;
}

.btn-generate-modern:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Results Section */
.results-section {
  padding: 20px 0;
}

.results-header {
  text-align: center;
  margin-bottom: 32px;
}

.results-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.results-title i {
  color: #10b981;
}

.results-summary {
  font-size: 16px;
  color: #64748b;
  margin: 0;
}

.results-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 32px;
}

/* Boleto Cards */
.boleto-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.3s ease;
}

.boleto-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.boleto-success {
  border-color: #10b981;
}

.boleto-error {
  border-color: #ef4444;
}

.boleto-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.boleto-success .boleto-card-header {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-bottom-color: #34d399;
}

.boleto-error .boleto-card-header {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-bottom-color: #f87171;
}

.boleto-status-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: white;
}

.boleto-success .boleto-status-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.boleto-error .boleto-status-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.boleto-number {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.boleto-card-content {
  padding: 20px;
}

.boleto-info-section {
  margin-bottom: 16px;
}

.boleto-client-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
  word-break: break-word;
}

.boleto-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.detail-item i {
  color: #6b7280;
  width: 12px;
}

.detail-label {
  color: #6b7280;
  font-weight: 500;
}

.detail-value {
  color: #1e293b;
  font-weight: 600;
}

.detail-value.amount {
  color: #10b981;
}

/* Actions */
.boleto-card-actions {
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
}

.success-actions {
  display: flex;
  gap: 8px;
}

.btn-action-view {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
  cursor: pointer;
  flex: 1;
}

.btn-action-view:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

.btn-action-download {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
  cursor: pointer;
  flex: 1;
}

.btn-action-download:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
}

.error-actions {
  text-align: center;
}

.error-message {
  color: #ef4444;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* Results Footer */
.results-footer {
  border-top: 1px solid #e5e7eb;
  padding-top: 24px;
}

.summary-stats {
  display: flex;
  justify-content: center;
  gap: 32px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-radius: 12px;
  background: white;
  border: 1px solid #e5e7eb;
}

.success-summary {
  border-color: #10b981;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.error-summary {
  border-color: #ef4444;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
}

.success-summary .summary-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.error-summary .summary-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.summary-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.summary-number {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.summary-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .boleto-content-modern {
    padding: 0;
  }

  .initial-card,
  .loading-card {
    padding: 24px;
  }

  .initial-title,
  .loading-title,
  .results-title {
    font-size: 20px;
  }

  .success-actions {
    flex-direction: column;
  }

  .summary-stats {
    flex-direction: column;
    gap: 16px;
  }

  .summary-item {
    justify-content: center;
    padding: 12px 20px;
  }

  .boleto-details {
    gap: 6px;
  }

  .detail-item {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .initial-stats {
    gap: 16px;
  }

  .btn-generate-modern {
    font-size: 14px !important;
    padding: 14px 24px !important;
  }

  .boleto-card-header {
    padding: 12px 16px;
  }

  .boleto-card-content {
    padding: 16px;
  }

  .boleto-client-name {
    font-size: 14px;
  }
}

/* Override Modal Styles */
.boleto-modal >>> .modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-radius: 0 !important;
}

.boleto-modal >>> .modal-body {
  padding: 24px !important;
}

.boleto-modal >>> .modal-content {
  border-radius: 16px !important;
  border: none !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}
</style>